import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { DYNTRA_LOGO_URL } from "@/lib/assets";

export default function AuthDialog() {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const isLogin = authMode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>{isLogin ? "Sign In" : "Create Account"}</DialogTitle>
          <DialogDescription>{isLogin ? "Sign in to your Dyntra Store account" : "Register for a new Dyntra Store account"}</DialogDescription>
        </VisuallyHidden>
        <div className="bg-gradient-to-br from-[#4B2AAD] to-[#6F44D4] p-6 text-white text-center">
          <div className="inline-flex items-center justify-center bg-white rounded-xl px-4 py-2 shadow-md mb-3">
            <img
              data-testid="auth-logo"
              src={DYNTRA_LOGO_URL}
              alt="DyntraStore"
              className="h-9 w-auto"
            />
          </div>
          <h2 data-testid="auth-heading" className="font-display text-2xl font-bold">
            {isLogin ? "Welcome Back" : "Create Your Account"}
          </h2>
          <p className="text-sm text-white/85 mt-1">
            {isLogin ? "Sign in to Dyntra Store" : "Join India's leading B2B dental platform"}
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {!isLogin && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Name</label>
              <div className="relative mt-1">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  data-testid="auth-name-input"
                  type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dr. Full Name"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/20 outline-none text-sm"
                />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Email</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                data-testid="auth-email-input"
                type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@clinic.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/20 outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Password</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                data-testid="auth-password-input"
                type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 6 characters"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/20 outline-none text-sm"
              />
            </div>
          </div>

          {error && <div data-testid="auth-error" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

          {isLogin && (
            <button
              type="button"
              data-testid="auth-demo-fill"
              onClick={() => setForm({ name: "", email: "demo@dyntrastore.test", password: "Demo@1234" })}
              className="w-full rounded-lg border border-dashed border-[#4B2AAD]/30 bg-[#F3EFFC]/40 hover:bg-[#F3EFFC] px-3 py-2 text-xs text-[#4B2AAD] font-semibold text-left"
            >
              Try the demo account — <span className="font-mono">demo@dyntrastore.test</span> / <span className="font-mono">Demo@1234</span>
              <span className="block text-[10px] font-normal opacity-70 mt-0.5">Click to fill</span>
            </button>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit" disabled={loading}
            className="dyntra-btn-primary w-full py-3 text-base"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isLogin ? "Sign In" : "Create Account")}
          </button>

          <div className="text-center text-sm text-gray-600">
            {isLogin ? "New here? " : "Already have an account? "}
            <button
              type="button"
              data-testid="auth-toggle-mode"
              onClick={() => { setAuthMode(isLogin ? "register" : "login"); setError(""); }}
              className="font-semibold text-[#4B2AAD] hover:underline"
            >
              {isLogin ? "Create account" : "Sign in"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
